/**
 * Change Tracking Admin API Client
 *
 * Enterprise-grade API client with:
 * - Strict typing using DTOs
 * - Proper error handling
 * - Centralized endpoint configuration
 */

export interface DashboardStats {
  recentChanges: number;
  pendingAggregations: number;
  severityBreakdown: Array<{ severity: string; count: number }>;
  topEntities: Array<{ entity_type: string; count: number }>;
  changeTrends: Array<{ date: string; count: number }>;
  systemHealth: {
    aggregationService: string;
    notificationService: string;
    databaseConnection: string;
  };
}

export interface ChangeFilters {
  limit?: number;
  offset?: number;
  entityType?: string;
  entityId?: string;
  changeType?: string;
  severity?: string;
  userId?: number;
  companyId?: number;
  startDate?: string;
  endDate?: string;
  search?: string;
}

export interface ChangesResponse {
  changes: any[];
  total: number;
  hasMore: boolean;
}

export interface Aggregation {
  id: number;
  aggregation_type: string;
  title: string;
  description?: string;
  affected_company_id?: number;
  severity: string;
  status: string;
  processed_at?: string;
  created_at: string;
  updated_at: string;
  created_by: number;
  updated_by: number;
  change_count: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  company_name?: string;
}

export interface NotificationPreference {
  id: number;
  user_id: number;
  notification_type: string;
  entity_types?: string[];
  change_types?: string[];
  severity_levels?: string[];
  enabled: boolean;
  created_at: string;
  updated_at: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  company_name?: string;
}

export interface SystemConfig {
  aggregationRules: any[];
  notificationSettings: {
    enabled: boolean;
    defaultPreferences: any;
  };
  retention: {
    changeHistoryDays: number;
    aggregationsDays: number;
    auditLogsDays: number;
  };
  performance: {
    maxChangesPerAggregation: number;
    aggregationIntervalMinutes: number;
  };
}

export interface AuditLog {
  id: number;
  action: string;
  user_id?: number;
  details?: any;
  created_at: string;
  first_name?: string;
  last_name?: string;
  email?: string;
}

/**
 * API Client Configuration
 */
const API_BASE = '/api/admin/change-tracking';

/**
 * Get authentication headers
 * Uses impersonation token if available, otherwise falls back to regular token
 */
function getAuthHeaders(): HeadersInit {
  // Try impersonation token first (for when admin is already impersonating)
  const impersonationToken = sessionStorage.getItem('impersonation_token');
  const token = localStorage.getItem('token');

  const authToken = impersonationToken || token;

  return {
    'Content-Type': 'application/json',
    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
  };
}

/**
 * Handle API response
 */
function handleResponse<T>(response: Response): Promise<T> {
  return response.json().then((data) => {
    if (!response.ok) {
      throw new Error(data.message || `HTTP ${response.status}`);
    }
    return data;
  });
}

export class ChangeTrackingAdminApi {
  /**
   * Get dashboard statistics
   */
  static async getDashboardStats(): Promise<DashboardStats> {
    const response = await fetch(`${API_BASE}/dashboard/stats`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<{ success: boolean; data: DashboardStats }>(response).then(res => res.data);
  }

  /**
   * Get recent changes feed
   */
  static async getRecentChanges(limit = 50, offset = 0): Promise<any[]> {
    const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() });
    const response = await fetch(`${API_BASE}/dashboard/recent-changes?${params}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<{ success: boolean; data: any[] }>(response).then(res => res.data);
  }

  /**
   * Get all changes with filtering
   */
  static async getAllChanges(filters: ChangeFilters = {}): Promise<ChangesResponse> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    const response = await fetch(`${API_BASE}/changes?${params}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<{ success: boolean; data: ChangesResponse }>(response).then(res => res.data);
  }

  /**
   * Get change aggregations
   */
  static async getAggregations(status?: string, limit = 50, offset = 0): Promise<Aggregation[]> {
    const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() });
    if (status) params.append('status', status);
    const response = await fetch(`${API_BASE}/aggregations?${params}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<{ success: boolean; data: Aggregation[] }>(response).then(res => res.data);
  }

  /**
   * Update aggregation status
   */
  static async updateAggregationStatus(id: number, status: string): Promise<void> {
    const response = await fetch(`${API_BASE}/aggregations/${id}/status`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ status }),
    });
    await handleResponse(response);
  }

  /**
   * Get notification preferences for all users
   */
  static async getNotificationPreferences(): Promise<NotificationPreference[]> {
    const response = await fetch(`${API_BASE}/notification-preferences`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<{ success: boolean; data: NotificationPreference[] }>(response).then(res => res.data);
  }

  /**
   * Update notification preferences for a user
   */
  static async updateNotificationPreferences(userId: number, preferences: any): Promise<void> {
    const response = await fetch(`${API_BASE}/notification-preferences/${userId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(preferences),
    });
    await handleResponse(response);
  }

  /**
   * Get system configuration
   */
  static async getSystemConfig(): Promise<SystemConfig> {
    const response = await fetch(`${API_BASE}/config`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<{ success: boolean; data: SystemConfig }>(response).then(res => res.data);
  }

  /**
   * Update system configuration
   */
  static async updateSystemConfig(config: Partial<SystemConfig>): Promise<void> {
    const response = await fetch(`${API_BASE}/config`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(config),
    });
    await handleResponse(response);
  }

  /**
   * Get audit logs
   */
  static async getAuditLogs(filters: {
    limit?: number;
    offset?: number;
    action?: string;
    userId?: number;
    startDate?: string;
    endDate?: string;
  } = {}): Promise<AuditLog[]> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    const response = await fetch(`${API_BASE}/audit?${params}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<{ success: boolean; data: AuditLog[] }>(response).then(res => res.data);
  }

  /**
   * Export changes data
   */
  static async exportChanges(filters: ChangeFilters & { format?: 'csv' | 'json' } = {}): Promise<any> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    const response = await fetch(`${API_BASE}/export?${params}`, {
      headers: getAuthHeaders(),
    });
    if (filters.format === 'csv') {
      return response.blob();
    }
    return handleResponse(response);
  }

  /**
   * Perform bulk operations
   */
  static async bulkOperations(operation: string, ids: number[], data?: any): Promise<void> {
    const response = await fetch(`${API_BASE}/bulk`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ operation, ids, data }),
    });
    await handleResponse(response);
  }

  /**
   * Assess rollback capability for a change
   */
  static async assessRollbackCapability(changeId: number): Promise<any> {
    const response = await fetch(`/api/admin/change-history/rollback-capability/${changeId}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<{ success: boolean; data: any }>(response).then(res => res.data);
  }

  /**
   * Generate rollback preview
   */
  static async generateRollbackPreview(changeId: number): Promise<any> {
    const response = await fetch(`/api/admin/change-history/rollback-preview/${changeId}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<{ success: boolean; data: any }>(response).then(res => res.data);
  }

  /**
   * Execute rollback
   */
  static async executeRollback(changeId: number): Promise<any> {
    const response = await fetch(`/api/admin/change-history/rollback/${changeId}`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleResponse<{ success: boolean; data: any }>(response).then(res => res.data);
  }

  /**
   * Analyze change impact
   */
  static async analyzeChangeImpact(changeId: number): Promise<any> {
    const response = await fetch(`/api/admin/change-history/impact-analysis/${changeId}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<{ success: boolean; data: any }>(response).then(res => res.data);
  }
}